/**
 * Product (SKU) form. uc-core CreateSkuDto / UpdateSkuDto (src/modules/pack/dto/pack.dto.ts):
 * code ≤ 20 (stored upper case), name ≤ 120, cropId, netG ≥ 1, gtin 8/12/13/14 digits with a GS1
 * check digit, shelfLifeDays ≥ 1, toleranceG "15" or "15.5" (one decimal), pouchesPerCarton ≥ 1.
 * Code, crop and pack size cannot change once made (the update takes none of them).
 */
import { cleanGtin, gtinProblem } from './gtin'

export interface SkuDraft {
  code: string
  name: string
  cropId: string
  netG: string
  gtin: string
  shelfLifeDays: string
  toleranceG: string
  pouchesPerCarton: string
}

export type SkuProblems = Partial<Record<keyof SkuDraft, string>>

export const emptySkuDraft = (): SkuDraft => ({
  code: '',
  name: '',
  cropId: '',
  netG: '',
  gtin: '',
  shelfLifeDays: '',
  toleranceG: '',
  pouchesPerCarton: '',
})

export function draftFromSku(s: {
  code: string
  name: string
  cropId: string
  netG: number
  gtin: string | null
  shelfLifeDays: number | null
  toleranceG: string | null
  pouchesPerCarton: number | null
}): SkuDraft {
  return {
    code: s.code,
    name: s.name,
    cropId: s.cropId,
    netG: String(s.netG),
    gtin: s.gtin ?? '',
    shelfLifeDays: s.shelfLifeDays === null ? '' : String(s.shelfLifeDays),
    toleranceG: s.toleranceG ?? '',
    pouchesPerCarton: s.pouchesPerCarton === null ? '' : String(s.pouchesPerCarton),
  }
}

/** Codes: capital letters, digits and dashes, as typed. */
export const normaliseSkuCode = (s: string) =>
  s.toUpperCase().replace(/\s+/g, '-').replace(/[^A-Z0-9-]/g, '').slice(0, 20)

const INT = /^\d{1,7}$/
const TOL = /^\d+(\.\d)?$/

const posInt = (s: string) => (INT.test(s.trim()) && Number(s.trim()) >= 1 ? Number(s.trim()) : null)

export function validateSkuDraft(d: SkuDraft, mode: 'create' | 'edit'): SkuProblems {
  const p: SkuProblems = {}
  if (mode === 'create') {
    if (!d.code) p.code = 'Enter a short code'
    else if (d.code.length < 2) p.code = 'At least 2 characters'
    if (!d.cropId) p.cropId = 'Pick the crop'
    if (!d.netG.trim()) p.netG = 'Enter the net weight of one pouch'
    else if (posInt(d.netG) === null) p.netG = 'Whole grams, e.g. 500'
  }
  const name = d.name.trim()
  if (!name) p.name = 'Enter the product name'
  else if (name.length > 120) p.name = 'At most 120 characters'
  const g = gtinProblem(d.gtin)
  if (g) p.gtin = g
  if (!d.shelfLifeDays.trim()) p.shelfLifeDays = 'Enter the shelf life — the best-before date comes from it'
  else if (posInt(d.shelfLifeDays) === null) p.shelfLifeDays = 'Whole days, e.g. 180'
  const tol = d.toleranceG.trim()
  if (tol) {
    if (!TOL.test(tol)) p.toleranceG = 'Grams with at most one decimal, e.g. 15 or 7.5'
    else {
      const net = posInt(d.netG)
      if (net !== null && Number(tol) >= net) p.toleranceG = 'Must be less than the net weight'
    }
  }
  if (d.pouchesPerCarton.trim() && posInt(d.pouchesPerCarton) === null) p.pouchesPerCarton = 'A whole number, e.g. 24'
  return p
}

export const skuOk = (p: SkuProblems) => Object.values(p).every((v) => !v)

/** Lowest acceptable pouch weight (net − tolerance), in grams; null when it cannot be worked out. */
export function minPouchG(d: Pick<SkuDraft, 'netG' | 'toleranceG'>): number | null {
  const net = posInt(d.netG)
  if (net === null) return null
  const tol = d.toleranceG.trim()
  if (!tol) return net
  if (!TOL.test(tol)) return null
  return (net * 10 - Math.round(Number(tol) * 10)) / 10
}

export interface CreateSkuBody {
  code: string
  name: string
  cropId: string
  netG: number
  gtin?: string
  shelfLifeDays?: number
  toleranceG?: string
  pouchesPerCarton?: number
}
export interface UpdateSkuBody {
  name: string
  gtin: string | null
  shelfLifeDays?: number
  toleranceG?: string
  pouchesPerCarton?: number
}

/** POST body. Call only when valid. */
export function createSkuBody(d: SkuDraft): CreateSkuBody {
  const gtin = cleanGtin(d.gtin)
  return {
    code: d.code,
    name: d.name.trim(),
    cropId: d.cropId,
    netG: Number(d.netG.trim()),
    ...(gtin ? { gtin } : {}),
    ...(d.shelfLifeDays.trim() ? { shelfLifeDays: Number(d.shelfLifeDays.trim()) } : {}),
    ...(d.toleranceG.trim() ? { toleranceG: d.toleranceG.trim() } : {}),
    ...(d.pouchesPerCarton.trim() ? { pouchesPerCarton: Number(d.pouchesPerCarton.trim()) } : {}),
  }
}

/** PATCH body: code, crop and pack size stay. An emptied GTIN is cleared. Call only when valid. */
export function updateSkuBody(d: SkuDraft): UpdateSkuBody {
  const gtin = cleanGtin(d.gtin)
  return {
    name: d.name.trim(),
    gtin: gtin || null,
    ...(d.shelfLifeDays.trim() ? { shelfLifeDays: Number(d.shelfLifeDays.trim()) } : {}),
    ...(d.toleranceG.trim() ? { toleranceG: d.toleranceG.trim() } : {}),
    ...(d.pouchesPerCarton.trim() ? { pouchesPerCarton: Number(d.pouchesPerCarton.trim()) } : {}),
  }
}
